import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { createFileRoute } from '@tanstack/react-router';
import { Building2, CreditCard, DollarSign, Users } from 'lucide-react';
import { useState } from 'react';

import { useAdminCompanies, useAdminDashboard } from '../../hooks/use-admin';
import { useDebounce } from '../../hooks/use-debounce';
import { useEnterCompany } from '../../hooks/use-enter-company';

export const Route = createFileRoute('/admin/dashboard')({
  component: AdminDashboardPage,
});

function AdminDashboardPage() {
  const { data: metrics, isLoading: metricsLoading, isError: metricsError } = useAdminDashboard();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const {
    data: companiesData,
    isLoading: companiesLoading,
    isError: companiesError,
  } = useAdminCompanies({
    search: debouncedSearch || undefined,
    limit: 10,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const enterCompany = useEnterCompany();

  const companies = companiesData?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Platform Overview</h1>
        <p className="text-muted-foreground">Monitor all companies and platform health.</p>
      </div>

      {/* Metrics Cards */}
      {metricsError && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load platform metrics. Please try refreshing the page.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Companies"
          value={metrics?.totalCompanies}
          icon={Building2}
          loading={metricsLoading}
        />
        <MetricCard
          title="Total Users"
          value={metrics?.totalUsers}
          icon={Users}
          loading={metricsLoading}
        />
        <MetricCard
          title="Active Subscriptions"
          value={metrics?.activeSubscriptions}
          icon={CreditCard}
          loading={metricsLoading}
          subtitle="Coming soon"
        />
        <MetricCard
          title="MRR"
          value={metrics?.mrr}
          icon={DollarSign}
          loading={metricsLoading}
          prefix="€"
          subtitle="Coming soon"
        />
      </div>

      {/* Companies Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Companies</CardTitle>
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {companiesError ? (
            <div className="py-8 text-center text-sm text-destructive">
              Failed to load companies.
            </div>
          ) : companiesLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : companies.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {search ? 'No companies match your search.' : 'No companies found.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Members</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.pid}>
                    <TableCell className="font-medium">{company.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{company.memberCount}</Badge>
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">
                      {company.seasonRegion}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          enterCompany({
                            pid: company.pid,
                            name: company.name,
                            slug: company.slug,
                          })
                        }
                      >
                        Enter →
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  title,
  value,
  icon: Icon,
  loading,
  prefix,
  subtitle,
}: {
  title: string;
  value: number | undefined;
  icon: React.ComponentType<{ className?: string }>;
  loading: boolean;
  prefix?: string;
  subtitle?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <>
            <div className="text-2xl font-bold">
              {prefix}
              {value?.toLocaleString() ?? 0}
            </div>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
